import { useState } from 'react';
import { Link } from 'react-router';

import { isLiabilityAccountType } from '@pfo/domain';

import { cx } from '../../../shared/lib/cx.js';
import { toAppError } from '../../../shared/lib/errors.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { EmptyState } from '../../../shared/ui/EmptyState.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import { AccountForm } from '../components/AccountForm.js';
import type { Account } from '../data/accountsRepository.js';
import { setAccountActive } from '../data/accountsRepository.js';
import { useAccounts } from '../hooks/useAccounts.js';
import { ACCOUNT_TYPE_LABELS, formatAccountBalance } from '../model/accountLabels.js';
import styles from './AccountsPage.module.css';

/**
 * "Tài sản" — what the household owns and owes, one row per account (plan step 1.1).
 *
 * There is no total on this screen on purpose: adding up money belongs in SQL, and the
 * figure the family wants (net worth, money flow) is the dashboard built in step 1.4.
 */
export function AccountsPage() {
  const contextState = useCurrentContext();
  const accountsState = useAccounts();
  const [isFormOpen, setFormOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [closingDebtId, setClosingDebtId] = useState<string | null>(null);

  if (contextState.status === 'loading' || accountsState.status === 'loading') {
    return <LoadingState label="Đang tải tài khoản…" />;
  }
  if (contextState.status === 'error') {
    return <ErrorState message={contextState.message} onRetry={contextState.retry} />;
  }
  if (accountsState.status === 'error') {
    return <ErrorState message={accountsState.message} onRetry={accountsState.retry} />;
  }

  const { accounts } = accountsState.overview;
  // Captured after the guards above: a hoisted function declaration is type-checked without
  // the narrowing those guards produce.
  const { refresh: refreshAccounts } = accountsState;
  const visible = accounts.filter((account) => showArchived || account.isActive);
  const assets = visible.filter((account) => !isLiabilityAccountType(account.accountType));
  const debts = visible.filter((account) => isLiabilityAccountType(account.accountType));
  const archivedCount = accounts.filter((account) => !account.isActive).length;

  // B5: a debt that once existed and is now at zero. Nothing happens automatically — the
  // ledger keeps the account and its history either way; this only offers to tidy the list.
  const settledDebts = accounts.filter(
    (account) =>
      account.isActive &&
      isLiabilityAccountType(account.accountType) &&
      account.hasMovement &&
      account.balance === 0,
  );

  async function closeSettledDebt(account: Account) {
    setMessage(null);
    try {
      await setAccountActive(account.id, false);
      setClosingDebtId(null);
      setNotice(`Đã đóng "${account.name}".`);
      await refreshAccounts();
    } catch (error) {
      setMessage(toAppError(error).message);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.heading}>
        <h1 className={styles.title}>Tài sản</h1>
        {!isFormOpen ? (
          <button type="button" className={styles.addButton} onClick={() => setFormOpen(true)}>
            + Thêm tài khoản
          </button>
        ) : null}
      </div>

      {notice ? (
        <p className={forms.success} role="status">
          {notice}
        </p>
      ) : null}
      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      {settledDebts.map((account) => (
        <section key={account.id} className={styles.settled} aria-label="Khoản nợ đã trả xong">
          <p className={styles.settledText}>
            <strong>{account.name}</strong> đã trả xong — đóng tài khoản này lại?
          </p>
          <div className={styles.settledActions}>
            {closingDebtId === account.id ? (
              <>
                <button
                  type="button"
                  className={forms.secondary}
                  onClick={() => setClosingDebtId(null)}
                >
                  Để nguyên
                </button>
                <button
                  type="button"
                  className={forms.primary}
                  onClick={() => void closeSettledDebt(account)}
                >
                  Đóng tài khoản
                </button>
              </>
            ) : (
              // Two taps, like every other close (skill 13.2): nothing is deleted, the account
              // is hidden and its history stays in the ledger.
              <button
                type="button"
                className={forms.secondary}
                onClick={() => setClosingDebtId(account.id)}
              >
                Đóng tài khoản
              </button>
            )}
          </div>
        </section>
      ))}

      {isFormOpen ? (
        <section className={lists.card}>
          <h2 className={lists.cardTitle}>Tài khoản mới</h2>
          <AccountForm
            context={{
              householdId: contextState.context.household.id,
              subjectId: contextState.context.selfSubject.id,
            }}
            timeZone={contextState.context.household.timezone}
            onCancel={() => setFormOpen(false)}
            onCreated={(result) => {
              setFormOpen(false);
              setNotice(
                result.openingBalanceFailed
                  ? 'Đã tạo tài khoản, nhưng chưa ghi được số dư đầu kỳ. Mở tài khoản để ghi lại.'
                  : 'Đã tạo tài khoản.',
              );
              void accountsState.refresh();
            }}
          />
        </section>
      ) : null}

      {accounts.length === 0 ? (
        <EmptyState
          icon="🏦"
          title="Chưa có tài khoản nào"
          message="Thêm tài khoản tiền mặt, ngân hàng, ví điện tử và các khoản nợ để bắt đầu ghi chép."
        />
      ) : (
        <>
          <AccountSection title="Tiền và tài sản" accounts={assets} />
          <AccountSection title="Nợ và thẻ tín dụng" accounts={debts} />

          {archivedCount > 0 ? (
            <label className={lists.toggle}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(event) => setShowArchived(event.target.checked)}
              />
              Hiện {archivedCount} tài khoản đã đóng
            </label>
          ) : null}
        </>
      )}
    </div>
  );
}

function AccountSection({ title, accounts }: { title: string; accounts: readonly Account[] }) {
  if (accounts.length === 0) return null;

  return (
    <section className={lists.card}>
      <h2 className={lists.cardTitle}>{title}</h2>
      <ul className={lists.list}>
        {accounts.map((account) => (
          <li key={account.id}>
            <div className={lists.row}>
              <Link className={cx(lists.rowLink)} to={`/tai-san/${account.id}`}>
                <span className={lists.rowText}>
                  <span className={lists.rowName}>{account.name}</span>
                  <span className={lists.rowMeta}>
                    {ACCOUNT_TYPE_LABELS[account.accountType]}
                    {account.institution ? ` · ${account.institution}` : ''}
                    {account.isDefault ? ' · Mặc định' : ''}
                    {account.isActive ? '' : ' · Đã đóng'}
                  </span>
                </span>
                <span
                  className={cx(
                    lists.amount,
                    isLiabilityAccountType(account.accountType) &&
                      account.balance < 0 &&
                      lists.amountDebt,
                  )}
                >
                  {formatAccountBalance(account.balance, account.accountType)}
                </span>
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
